﻿import {nanoid, StringNamedValue} from "@2d-front-monorepo/core";
import React, {ChangeEvent, JSX, ReactNode, useMemo, useRef} from "react";
import {useArrayOnChange} from "./useArrayOnChange";
import {useSelected} from "./useSelected";

export type OptionGroupEntry = StringNamedValue & {
	disabled?: boolean;
}

export type OptionGroupProps = {
	label: ReactNode,
	name: string,
	values: string[],
	items: OptionGroupEntry[];
	column?: string,
	disabled?: boolean;
	hidden?: boolean;
	onChange?: (values: number[]) => void;
	ref?: React.RefObject<HTMLSpanElement | null>;
	multiple?: boolean;
};

type RenderItemProps = {
	label: ReactNode;
	name: string;
	value: string;
	selected: boolean;
	type: "checkbox" | "radio";
	disabled?: boolean;
	itemDisabled?: boolean;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

function RenderItem(props: RenderItemProps): JSX.Element {
	const {label, name, value, selected, type, disabled, itemDisabled, onChange} = props;
	const controlId = useMemo(() => "cbg-" + nanoid(), []);
	const isDisabled = disabled || itemDisabled;
	return <>
		<input type={type}
		       id={controlId}
		       name={name}
		       value={value}
		       checked={selected}
		       tabIndex={isDisabled ? -1 : undefined}
		       disabled={isDisabled}
		       onChange={onChange}/>
		<label htmlFor={controlId}>{label}</label>
	</>;
}

export default function OptionGroup(props: OptionGroupProps): JSX.Element {
	const {
		label,
		name,
		values,
		items,
		column,
		hidden,
		disabled,
		onChange,
		ref,
		multiple
	} = props;

	const klass = useMemo(() => "labelled-field" + (column ? ` ${column}` : ""), [column]);
	const type = multiple ? "checkbox" : "radio";
	const role = multiple ? "group" : "radiogroup";
	const innerRef = ref ?? useRef<HTMLSpanElement>(null);

	const selected = useSelected(values, multiple);
	const internalOnChange = useArrayOnChange(innerRef, onChange);

	if (hidden) {
		return <></>;
	}

	return <div className={klass}>
		<div className="form__field">
			<label className="field__label">
				<span className="label__text">{label}</span>
			</label>
			<span className="field__options" role={role} ref={innerRef}>
				{items.map((item, index) => <RenderItem label={item.label}
				                                        value={item.value}
				                                        key={index}
				                                        name={name}
				                                        selected={selected(item.value)}
				                                        type={type}
				                                        disabled={disabled}
				                                        itemDisabled={item.disabled}
				                                        onChange={internalOnChange}/>)}
			</span>
		</div>
	</div>;
};
